// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  ChartEvent,
  Point,
  PointElement,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  Filler,
  TimeUnit,
} from "chart.js";
import { AnnotationOptions } from "chartjs-plugin-annotation";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";
import ResizeObserver from "resize-observer-polyfill";

import { isSet, formatTimestamp } from "utils";
import { TimePeriod } from "types";
import { hexToRgba, getGraphBoundaryDates, getColor, getIoType, getSiteSelection } from "guiutils";
import { COLOR_SELECT, getGraphMinUnits } from "guiutils";
import { ActionType, IAction, raiseAction, IActionTypeZoom, IActionTypeSelectIos } from "actions";
import { IState } from "states/states";
import { IDropdownSelectOption } from "./DropdownSelect";

import { IInput, IOutput, OutputMode } from "parameter";
import { IIoTrend } from "states/trendingstates";

import { getAllAvailableIos, getSelectedIos } from "view/page/IoTrend";
import { ReactComponent as IconZoomFit } from "static/iconZoomFit.svg";
import cssContent from "content.scss";
import { Aggregation } from "graphql/api";

// if we have more than this items in the legend, we hide zero values in the tooltip
const NR_TLS_HIDE_ZERO = 10;
const MAX_DIGIAL_LINE_WIDTH = 50;
const FONTSIZE_REDUCTION = 0.1;
const OFFSET_LINECOUNT_CORRECTION = 0.05;
const DIGITAL_GRAPH_FONTSIZE = 15;
const DIGITAL_GRAPH_LINE_SPACE_HEIGHT = 2;
const DIGITAL_GRAPH_LINE_HEIGHT = 1;
const ANALOG_GRAPH_LINE_WIDTH = 2;

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  zoomPlugin,
  Filler,
);

interface IIoGraphExtProps {
  aggregation: Aggregation;
}

interface IIoGraphProps {
  selectedIoType: "digital" | "analog";
  isLoading: boolean;
  queryMoreData: boolean;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
  lastTrendData: number | undefined;
  ioTrend: IIoTrend;
  showAllData: boolean;
  startDate: number | undefined;
  endDate: number | undefined;
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  // deviceTimestamp: number | undefined;
  timezoneOffset: number | undefined;
  input: IInput[] | undefined;
  output: IOutput[] | undefined;
}

const mapStateToProps = (state: IState, ownProps: IIoGraphExtProps) => {
  const selectedIoType = getIoType(state);
  const showAllData = ownProps.aggregation === Aggregation.NONE || selectedIoType === "digital";
  const siteSelection = getSiteSelection(state);

  const props: IIoGraphProps = {
    selectedIoType: getIoType(state),
    isLoading: state.scheduler.isIoTrendLoading,
    // we have all data if no aggr. data is available
    queryMoreData: state.trending.queryMoreData,
    lastTrendData: state.trending.maxTrendTimestamp,
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
    ioTrend: showAllData ? state.trending.ioTrendFull : state.trending.ioTrendAggr,
    showAllData,
    startDate: state.gui.ioPeriod.startDate,
    endDate: state.gui.ioPeriod.endDate,
    xZoom: state.gui.ioPeriod.timePeriod,
    minX: state.gui.ioPeriod.zoomMin,
    maxX: state.gui.ioPeriod.zoomMax,
    timezoneOffset: state.site.tzOffset,
    input: state.parameter?.input,
    output: state.parameter?.output,
  };
  return props;
};

interface IIoGraphDispatch {
  onZooming: (minX?: number, maxX?: number) => void;
  onDropdownIOSelectionChanged: (
    type: "digital" | "analog",
    inputIds: number[],
    outputIds: number[],
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IIoGraphDispatch = {
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "ioPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
    onDropdownIOSelectionChanged: (type, inputIds, outputIds) => {
      const payload: IActionTypeSelectIos = { type, inputIds, outputIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_IOS, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
  labelOn: {
    id: "On",
  },
  labelOff: {
    id: "Off",
  },
  labelMixed: {
    id: "Mixed",
  },
  timeOn: {
    id: "On Time",
  },
  timeOff: {
    id: "Off Time",
  },
  activities: {
    id: "Activities",
  },
  loading: {
    id: "Data loading...",
  },
});

interface IGraphLayout {
  fontSize: number;
  maxLineWidth: number;
  maxGraphHeight: number;
  maxGraphWidth: number;
  lineCnt: number;
  maxY: number;
  padding: number;
}

interface ITimeStats {
  lastState: number | undefined;
  lastTimeStamp: number;
  timeOff: number;
  timeOn: number;
  sumTimeOff: number;
  sumTimeOn: number;
  count: number;
}

// State is never set so we use the 'undefined' type.
class IoGraphComponent extends React.Component<
  IIoGraphExtProps & IIoGraphProps & IIoGraphDispatch & WrappedComponentProps
> {
  private resizeObserver: ResizeObserver | undefined;
  private chartRef = React.createRef<Chart>();
  private readonly compRef = React.createRef<HTMLDivElement>();
  private startTime?: number;
  private endTime?: number;
  private maxValue: number | undefined;
  private data: [number[][]] = [[]]; // array of [timestamp, y] values for each graph
  private oldHeight = 0;
  private height = 0;
  // for each selectedIO!!!
  private timeStats: ITimeStats[] = [];
  private minUnits: TimeUnit = "month";
  private selectedOptions: IDropdownSelectOption[] = [];
  private zoomLock = false;
  private panLock = false;
  private parameter?: (IInput | IOutput | undefined)[] = [];

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((elem: ResizeObserverEntry[]) => {
      if (this.chartRef.current && elem[0].contentRect.height) {
        // yes, this is odd
        // on screen build-up on the dashboard this graphs parent div gets resized twice
        // however, the graph just resizes the first one. We need to force screen update
        // by unmount/remount which we achieve by changing the divs key attribute
        // since the first resize is OK, we use oldHeight and a 2-stage FIFO
        this.oldHeight = this.height;
        this.height = elem[0].contentRect.height;
      }
    });
    if (this.compRef.current) this.resizeObserver.observe(this.compRef.current);
    else console.warn("did not register graph size observer");
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  public render() {
    const allIos = getAllAvailableIos(
      [this.props.input ?? [], this.props.output ?? []],
      this.props.selectedIoType,
    );
    const selectedOptions = getSelectedIos(
      allIos,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      true,
    );
    this.selectedOptions = [...selectedOptions[0], ...selectedOptions[1]];
    // the legend seems to be ordered in reverse order, so we need to reverse the data set
    this.selectedOptions = this.selectedOptions.reverse();

    this.parameter = this.selectedOptions.map((curItem) =>
      curItem.source === "input"
        ? this.props.input?.[curItem.index]
        : this.props.output?.[curItem.index],
    );

    // make some general calculations
    this.preProcessData();

    if (this.props.isLoading || this.props.queryMoreData) {
      return (
        <div id="iograph" className="graph" ref={this.compRef}>
          <div className="loading-spinner-box">
            <div id="loading-spinner"></div>
          </div>
        </div>
      );
    }
    const graphLayout = this.prepareGraphLayout();
    return (
      <div id="iograph" className="graph" ref={this.compRef} key={this.oldHeight}>
        {isSet(this.props.minX) || isSet(this.props.maxX) ? (
          <div
            className="graph-zoom"
            onClick={this.resetZoom.bind(this)}
            title={this.props.intl.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        ) : (
          <></>
        )}
        {(this.props.isLoading || this.props.queryMoreData) && <div id="loading-spinner" />}
        <Chart
          ref={(c) => {
            if (isSet(c)) this.chartRef = c as any;
          }}
          type="line"
          data={this.createGraphData(graphLayout)}
          options={this.createGraphOptions(graphLayout)}
        />
      </div>
    );
  }

  private preProcessData() {
    // some general data pre-processing
    this.data = [[]];
    // for each selectedIO!!!
    this.timeStats = [];

    const oldMax = this.maxValue;
    this.maxValue = undefined;

    // prepare a timestat instance for each selectedIo
    this.timeStats = this.selectedOptions.map(() => {
      const timeStat: ITimeStats = {
        lastState: undefined,
        lastTimeStamp: 0,
        timeOff: 0,
        timeOn: 0,
        sumTimeOn: 0,
        sumTimeOff: 0,
        count: 0,
      };
      return timeStat;
    });

    const graphDates = getGraphBoundaryDates(
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.xZoom,
    );
    this.selectedOptions.forEach((selectedIo, dataIndex) => {
      const sourceIndex = selectedIo?.source === "input" ? 0 : 1;
      const timeStat = this.timeStats[dataIndex];

      this.startTime = this.props.minX ? this.props.minX / 1000 : graphDates.startDate;
      this.endTime = this.props.maxX ? this.props.maxX / 1000 : graphDates.endDate;
      this.minUnits = getGraphMinUnits(this.endTime! - this.startTime!);
      const isInverted =
        selectedIo.source === "output" &&
        (this.parameter?.[dataIndex] as IOutput | undefined)?.mode === OutputMode.OPEN;

      // prepare data over the whole graph!! (otherwise the lines won't get drawn)
      const startTimeData = graphDates.startDate!;
      const endTimeData = graphDates.endDate!;

      const allTimestamps = Object.keys(this.props.ioTrend).map((item) => parseInt(item));
      const dataTimestamps =
        selectedIo !== undefined
          ? allTimestamps.filter((curItem) => curItem >= startTimeData && curItem < endTimeData)
          : [];
      // find very first time-entry of the current io
      const firstEntry = allTimestamps.find(
        (timestamp) =>
          timestamp < startTimeData &&
          this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] !== undefined,
      );
      // find very last time-entry of the current io
      const lastEntry = allTimestamps.find(
        (timestamp) =>
          timestamp >= endTimeData &&
          this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] !== undefined,
      );

      this.data[dataIndex] = [];
      if (selectedIo.visible === true) {
        // not all timestamps may have a value for this IO associated, therefore we cannot use map()
        dataTimestamps.forEach((curTimestamp) => {
          let curValue = this.props.ioTrend[curTimestamp][sourceIndex]?.[selectedIo.index];
          if (!isNaN(curValue)) {
            // only invert on digital signals
            if (this.props.selectedIoType === "digital" && isInverted) {
              curValue = curValue ? 0 : 1;
            }
            // derive maximum
            if (this.maxValue === undefined || this.maxValue < curValue) {
              this.maxValue = curValue;
            }
            // derive firstState
            if (this.props.showAllData) {
              // do some timing-statistics
              if (!isSet(timeStat.lastState)) {
                // check if we found any...
                if (isSet(firstEntry)) {
                  // if so -> load it -- if the value is undefined - leave state undefined (no sensor until now!)
                  // set the first valid value to the timestamp available (this indicates, that the sensor was connected after
                  // the startime of our graph!)
                  timeStat.lastState =
                    (this.props.ioTrend[firstEntry]?.[sourceIndex]?.[selectedIo.index] as
                      | number
                      | undefined) ?? undefined;
                  // only invert on digital signals
                  if (
                    this.props.selectedIoType === "digital" &&
                    isSet(timeStat.lastState) &&
                    isInverted
                  ) {
                    timeStat.lastState = timeStat.lastState ? 0 : 1;
                  }
                }

                // check if we found state before the first point we want to add...
                if (isSet(timeStat.lastState)) {
                  // if yes -> use that state from the graph-start-date to the current timestamp
                  // check if the graph has a start-date
                  if (isSet(firstEntry)) {
                    // set first value we want to add
                    // timeStat.lastTimeStamp = graphStartTimestamp;

                    // add first point avaialable (if any) to the graph -- this point might and will be outside of
                    // the viewable area - so the curve is drawn correctly.
                    // only counterside -> if the point is within 100 seconds to the first graphstart-timestamp
                    // the point including the tooltip is visible (with the correct date of yesterday!)
                    // to get rid of this, we need to check if the first entry is within 100 seconds or fake that point 100seconds to the past
                    this.data[dataIndex].push([firstEntry, timeStat.lastState]);
                  }
                } else {
                  // if no state was found - take the current one as the very first one ever available
                  timeStat.lastState = curValue;
                }

                if (timeStat.lastTimeStamp === 0) {
                  // there was not state bofore the frist timestamp availble OR there is no valid startDate in our Graph set...
                  // first one indicates, that the sensor was conencted and this timestamp
                  // holds the very first value. So start here with all calculations and
                  // drawings
                  // if there is no startDate in our graph available - take the found value as the first one
                  timeStat.lastTimeStamp = curTimestamp;
                }

                timeStat.count = 0;
              }
            }

            // -> add current point anyway (even if the curTimestamp is equal to graphDates.startDate)

            this.data[dataIndex].push([curTimestamp, curValue]);

            // prepare timing-statistic --> only if digital and if all data is available!
            if (!this.props.queryMoreData) {
              if (this.props.selectedIoType === "digital") {
                timeStat.count += 1;
                if (timeStat.lastState === 1) {
                  timeStat.timeOn = curTimestamp - timeStat.lastTimeStamp;
                  timeStat.sumTimeOn += timeStat.timeOn;
                } else {
                  timeStat.timeOff = curTimestamp - timeStat.lastTimeStamp;
                  timeStat.sumTimeOff += timeStat.timeOff;
                }
              }
              timeStat.lastTimeStamp = curTimestamp;
              timeStat.lastState = curValue;
            }
          }
        });

        // set the last value derived by the backend directly for analog signals
        // so the curve is drawn correctly
        // only counterside -> if the point is within 100 seconds to the last graphstart-timestamp
        // the point including the tooltip is visible (with the correct date of next day!)
        // to get rid of this, we would need to check if the last entry is within 100 seconds or
        // fake that point 100 seconds to the future
        if (
          this.props.selectedIoType === "analog" &&
          isSet(lastEntry) &&
          this.data[dataIndex].length > 0
        ) {
          const veryLastVal = this.props.ioTrend[lastEntry][sourceIndex]?.[selectedIo.index];

          this.data[dataIndex].push([lastEntry, veryLastVal]);
        }

        // check if we got any point!
        if (this.data[dataIndex].length === 0 && graphDates.startDate) {
          // assume OFF/0 as state if we have no data? better not, just don't show it
          timeStat.lastState = undefined;

          // check if we found a first-timestamp
          const firstTimestamp = Object.keys(this.props.ioTrend)
            .map((item) => parseInt(item))
            .filter((timestamp) => {
              return (
                (this.props.ioTrend[timestamp]?.[sourceIndex]?.[selectedIo.index] as
                  | number
                  | undefined) !== undefined
              );
            });

          if (firstTimestamp.length > 0) {
            timeStat.lastState =
              (this.props.ioTrend[firstTimestamp[0]]?.[sourceIndex]?.[selectedIo.index] as
                | number
                | undefined) ?? 0;
            // only invert on digital signals
            if (
              this.props.selectedIoType === "digital" &&
              isSet(timeStat.lastState) &&
              isInverted
            ) {
              timeStat.lastState = timeStat.lastState ? 0 : 1;
            }
          }

          if (graphDates.startDate && isSet(timeStat.lastState)) {
            // set first value we want to add
            timeStat.lastTimeStamp = graphDates.startDate;

            // add point at very beginning of the chart
            this.data[dataIndex].push([graphDates.startDate, timeStat.lastState]);
          }

          // prepare timing-statistic --> only if digital and if all data is available!
          if (this.props.selectedIoType === "digital" && !this.props.queryMoreData) {
            timeStat.count += 1;
            // statistic values are still 0 - so no need to update it here!
            const dateFormat = new Date(timeStat.lastTimeStamp * 1000);
            dateFormat.setHours(0);
            dateFormat.setMinutes(0);
            dateFormat.setSeconds(0);
            timeStat.lastTimeStamp = dateFormat.valueOf() / 1000;
            timeStat.count = 0;
          }
        }

        if (isSet(timeStat.lastState) && isSet(this.props.lastTrendData) && graphDates.endDate) {
          // add point at very end of chart (or last received io data)
          const endDate = Math.max(
            Math.min(this.props.lastTrendData, graphDates.endDate),
            timeStat.lastTimeStamp,
          );

          if (this.props.selectedIoType === "digital") {
            this.data[dataIndex].push([endDate, timeStat.lastState]);
            if (timeStat.lastState === 1) {
              timeStat.timeOn = endDate - timeStat.lastTimeStamp;
              timeStat.sumTimeOn += timeStat.timeOn;
            } else {
              timeStat.timeOff = endDate - timeStat.lastTimeStamp;
              timeStat.sumTimeOff += timeStat.timeOff;
            }
          } else if (
            this.data[dataIndex].length > 0 &&
            timeStat.lastTimeStamp < endDate &&
            (!isSet(lastEntry) || lastEntry < endDate)
          ) {
            // if any point is available, we need to add a last point (last IO data received)
            // if no point is available, we don't need to add a point (no line should get drawn!)
            this.data[dataIndex].push([endDate, timeStat.lastState]);
          }
          timeStat.lastTimeStamp = endDate;
        }
      }
    });

    if (isSet(this.maxValue)) {
      this.maxValue = oldMax;
    }
    this.maxValue = isSet(this.maxValue) ? Math.round(this.maxValue * 1.05) : undefined;
  }

  private legendItemClick(evt: ChartEvent, legendItem: LegendItem, legend: any) {
    const index = legendItem.datasetIndex;
    const ci = legend.chart;
    let newSelectedIos: IDropdownSelectOption[] = [];

    if (index === undefined) {
      return;
    }

    if ((evt.native as PointerEvent).shiftKey) {
      // check if any curve is already hidden...
      const filtered = this.selectedOptions.filter((io) => io.visible === false);
      const anyHidden = filtered ? filtered.length > 0 : 0;

      // check if the clicked curve is hidden AND if any other curve is also hidden
      if (legendItem.hidden === false && anyHidden) {
        // if yes: show all again
        newSelectedIos = this.selectedOptions.map((anItem) => {
          // update entry
          anItem.visible = true;
          return anItem;
        });
      } else {
        // otherwise: hide all except current one.... (leave var empty)
        newSelectedIos = this.selectedOptions
          .filter((curItem, curIndex) => {
            return curIndex === index;
          })
          .map((anItem) => {
            // update entry
            anItem.visible = Boolean(legendItem.hidden);
            return anItem;
          });
      }
    } else {
      newSelectedIos = this.selectedOptions
        .map((curItem, curIndex) => {
          if (curIndex === index) {
            // update entry
            curItem.visible = Boolean(legendItem.hidden);
          }
          return curItem;
        })
        .filter((item) => item.visible);
    }
    this.props.onDropdownIOSelectionChanged(
      this.props.selectedIoType,
      newSelectedIos.filter((curItem) => curItem.source === "input").map((curItem) => curItem.id),
      newSelectedIos.filter((curItem) => curItem.source === "output").map((curItem) => curItem.id),
    );
  }

  private prepareGraphLayout(): IGraphLayout {
    // grap the current component-height
    const maxGraphHeight = this.compRef.current?.clientHeight;
    const maxGraphWidth = this.compRef.current?.clientWidth;
    // try to use the selected lines (via DropdownMenu) count - if not available then all lines are selected
    // so use the this.selectedIos -- if this is not available (something went wrong or no data avialable) use
    // an average value - but since no line is drawn then it doesn't matter
    const lineCnt = this.selectedOptions.filter((curItem) => curItem.visible).length;
    const availableSpace = maxGraphHeight! / lineCnt / 3;
    const maxLineWidth =
      availableSpace > MAX_DIGIAL_LINE_WIDTH ? MAX_DIGIAL_LINE_WIDTH : availableSpace;
    const fontSize = DIGITAL_GRAPH_FONTSIZE - lineCnt * FONTSIZE_REDUCTION;

    // only use lineCount on digital-lines - use calculated maxValue for analog-values
    const maxY =
      this.props.selectedIoType === "digital"
        ? lineCnt * DIGITAL_GRAPH_LINE_SPACE_HEIGHT + lineCnt * OFFSET_LINECOUNT_CORRECTION
        : undefined; // do not use this.maxValue as this yields problems when only an output is selected

    const padding =
      1 + maxLineWidth / DIGITAL_GRAPH_LINE_SPACE_HEIGHT - lineCnt * OFFSET_LINECOUNT_CORRECTION;

    return {
      fontSize,
      maxLineWidth,
      maxGraphHeight,
      maxGraphWidth,
      lineCnt,
      maxY,
      padding,
    } as IGraphLayout;
  }

  private createGraphLabels(graphLayout: IGraphLayout) {
    const labels: AnnotationOptions[] = [];
    let lineIndex = 0;
    this.parameter?.forEach((aParameter, dataIndex) => {
      const selectedIo = this.selectedOptions[dataIndex];
      if (isSet(selectedIo) && selectedIo.visible === true) {
        const labelName: AnnotationOptions = {
          type: "label",
          xValue: this.startTime ? this.startTime * 1000 : this.props.startDate,
          yValue: lineIndex * DIGITAL_GRAPH_LINE_SPACE_HEIGHT + 1,
          padding: graphLayout.padding,
          color: getColor(selectedIo.index),
          position: { x: "start", y: "end" },
          backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
          content: aParameter?.name ?? "----",
          font: {
            family: "Arial",
            size: graphLayout.fontSize,
            style: "normal",
            lineHeight: 1,
            weight: null,
          },
        };

        const contentText =
          graphLayout.maxGraphWidth && graphLayout.maxGraphWidth < 480
            ? graphLayout.maxGraphWidth && graphLayout.maxGraphWidth < 320
              ? ""
              : formatTimestamp(this.timeStats[dataIndex].sumTimeOn) +
                ", " +
                formatTimestamp(this.timeStats[dataIndex].sumTimeOff) +
                ", " +
                String(this.timeStats[dataIndex].count)
            : this.props.intl.formatMessage(messages.timeOn) +
              ": " +
              formatTimestamp(this.timeStats[dataIndex].sumTimeOn) +
              ", " +
              this.props.intl.formatMessage(messages.timeOff) +
              ": " +
              formatTimestamp(this.timeStats[dataIndex].sumTimeOff) +
              ", " +
              this.props.intl.formatMessage(messages.activities) +
              ": " +
              String(this.timeStats[dataIndex].count);

        const labelStatistic: AnnotationOptions = {
          type: "label",
          xValue: this.endTime ? this.endTime * 1000 : this.props.maxX,
          yValue: lineIndex * DIGITAL_GRAPH_LINE_SPACE_HEIGHT + DIGITAL_GRAPH_LINE_HEIGHT,
          padding: graphLayout.padding,
          color: getColor(selectedIo.index),
          position: { x: "end", y: "end" },
          backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
          content: contentText,
          font: {
            family: "Arial",
            size: graphLayout.fontSize,
            style: "normal",
            lineHeight: 1,
            weight: null,
          },
        };
        labels.push(labelName);
        labels.push(labelStatistic);

        lineIndex = lineIndex + 1;
      }
    });
    return labels;
  }

  private createGraphOptions(graphLayout: IGraphLayout) {
    const isLegendTop = this.selectedOptions.length <= NR_TLS_HIDE_ZERO;
    const options: ChartOptions<"line"> = {
      onResize: (chart, size) => this.resizeGraph(chart, size),
      scales: {
        x: {
          type: "time",
          time: {
            //unit: this.useBarChart ? "hour" : "day",
            minUnit: this.minUnits,
            displayFormats: {
              minute: this.props.intl.formatMessage(messages.graphFormatAxisMinute),
              hour: this.props.intl.formatMessage(messages.graphFormatAxisHour),
              day: this.props.intl.formatMessage(messages.graphFormatAxisDay),
            },
          },
          min: this.startTime ? this.startTime * 1000 : undefined,
          // set max date one month earlier at year view?
          max: this.endTime ? this.endTime * 1000 : undefined,
          stacked: true, // only relevant for bar chart
        },
        y: {
          type: "linear",
          max: graphLayout.maxY,
          min: this.props.selectedIoType === "digital" ? 0 : undefined,
          beginAtZero: true,
          display: this.props.selectedIoType === "analog",
        },
      },
      animation: {
        duration: this.props.selectedIoType === "digital" ? 0 : 1000,
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      elements: {
        point: {
          radius: this.props.selectedIoType === "digital" ? 0 : 2,
        },
      },
      datasets: {
        line: {
          tension: 0.2, // line smoothness, default 0; use less here to get realistic SoC
        },
      },
      plugins: {
        legend: {
          display:
            this.props.selectedIoType === "analog" &&
            this.selectedOptions.length > 1 &&
            (window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop),
          reverse: true,
          position: isLegendTop ? "top" : "right",
          onClick: (evt, legendItem, legend) => this.legendItemClick(evt, legendItem, legend),
        },
        annotation: {
          annotations:
            this.props.selectedIoType === "digital" ? this.createGraphLabels(graphLayout) : [],
        },
        tooltip: {
          enabled: true,
          mode: "point", // show all data items on same x value
          position: "nearest", // show tooltip on average value, not at mouse cursor
          intersect: this.props.selectedIoType === "analog", // for digital IOs, no need to exactly match the point to show the tooltip
          itemSort: (a, b) => {
            return b.datasetIndex - a.datasetIndex;
          },
          callbacks: {
            title: (tooltipItems: TooltipItem<"line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: this.props.xZoom !== TimePeriod.YEAR ? "numeric" : undefined,
                hour:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
                minute:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
              };
              return date.toLocaleString(this.props.intl.locale, localeOptions);
            },
            label: (tooltipItem: TooltipItem<"bar" | "line">) => {
              // do we face a background fill data set?
              if (
                tooltipItem.dataset.label === undefined ||
                this.selectedOptions[tooltipItem.dataIndex] === undefined ||
                this.selectedOptions[tooltipItem.dataIndex].visible === false
              ) {
                return undefined as unknown as string; // we need to disable the wrong type check here
              }
              let value: string = tooltipItem.formattedValue;
              if (this.props.selectedIoType === "digital") {
                if (this.data[tooltipItem.datasetIndex][tooltipItem.dataIndex]?.[1] === 0) {
                  value = this.props.intl.formatMessage(messages.labelOff);
                } else if (this.data[tooltipItem.datasetIndex][tooltipItem.dataIndex]?.[1] === 1) {
                  value = this.props.intl.formatMessage(messages.labelOn);
                } else {
                  value = this.props.intl.formatMessage(messages.labelMixed);
                }
              }
              return ` ${tooltipItem.dataset.label}: ${value}`;
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            modifierKey: "shift",
            onPanStart: (context) => this.panStartCallback(context.chart, context.point),
            onPanComplete: () => this.panCompleteCallback(),
            onPanRejected: () => this.panCompleteCallback(),
          },
          zoom: {
            mode: "x",
            wheel: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderColor: hexToRgba(COLOR_SELECT, 0.7),
              backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
            },
            pinch: {
              enabled: true,
            },
            onZoom: (context) => this.zoomCallback(context.chart),
            onZoomStart: (context) => this.zoomCheck(context.chart, context.point),
            onZoomComplete: () => this.zoomCompleteCallback(),
            onZoomRejected: () => this.zoomCompleteCallback(),
          },
          limits: {
            x: {
              min: "original",
              max: "original",
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
    };

    return options;
  }

  private createGraphData(graphLayout: IGraphLayout) {
    const data: ChartData<"line", ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    if (this.props.isLoading || this.props.queryMoreData) {
      // no data yet
      return data;
    }

    let lineIndex = 0;
    this.data.forEach((dataset, anIndex) => {
      if (
        this.selectedOptions[anIndex]?.visible === true ||
        this.props.selectedIoType === "analog"
      ) {
        const aColorIndex = this.selectedOptions[anIndex]?.index ?? 0;
        const points: ScatterDataPoint[] =
          this.selectedOptions[anIndex]?.visible === true
            ? dataset.map((curItem, ind) => {
                return {
                  x: curItem[0] * 1000, // timstamp
                  y:
                    this.props.selectedIoType === "digital"
                      ? curItem[1]
                        ? DIGITAL_GRAPH_LINE_SPACE_HEIGHT * lineIndex +
                          (DIGITAL_GRAPH_LINE_HEIGHT + 0.0000000001)
                        : DIGITAL_GRAPH_LINE_SPACE_HEIGHT * lineIndex + DIGITAL_GRAPH_LINE_HEIGHT
                      : curItem[1],
                };
              })
            : [];

        data.datasets.push({
          label: this.parameter?.[anIndex]?.name,
          data: points,
          type: "line",
          stepped: this.props.selectedIoType === "digital",
          fill: false,
          hidden: this.selectedOptions[anIndex]?.visible === false,
          segment: {
            borderColor: (ctx) => {
              if (this.props.selectedIoType === "analog") return undefined;

              const value0 = dataset[ctx.p0DataIndex]?.[1];
              const value1 = dataset[ctx.p1DataIndex]?.[1];
              if (value1 < value0) {
                return cssContent.GRAPH_COLOR;
              } else if (value1 === value0) {
                return value1 === 1 ? cssContent.GRAPH_COLOR : cssContent.GRAPH_COLOR_OVERSHOOT;
              }
              return cssContent.GRAPH_COLOR_OVERSHOOT;
            },
          },
          borderWidth:
            this.props.selectedIoType === "digital"
              ? graphLayout.maxLineWidth
              : ANALOG_GRAPH_LINE_WIDTH,
          borderColor:
            this.props.selectedIoType === "digital"
              ? cssContent.DROPDOWN_TRANSPARENT
              : getColor(aColorIndex),
          backgroundColor: getColor(aColorIndex),
        });

        lineIndex = lineIndex + 1;
      }
    });
    return data;
  }

  private zoomCallback(chart: ChartJS) {
    const axis = chart.scales.x;
    this.props.onZooming(axis.min, axis.max);
    const graphLayout = this.prepareGraphLayout();
    chart.options = this.createGraphOptions(graphLayout);
    chart.update();
  }

  private resetZoom() {
    this.props.onZooming();
  }

  private zoomCheck(chart: ChartJS, point: Point) {
    this.panLock =
      !this.zoomLock &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;
    return this.panLock;
  }

  private zoomCompleteCallback() {
    this.panLock = false;
  }

  private panStartCallback(chart: ChartJS, point: Point) {
    this.zoomLock =
      !this.panLock &&
      this.props.minX !== undefined &&
      this.props.maxX !== undefined &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;

    return this.zoomLock;
  }
  private panCompleteCallback() {
    this.zoomLock = false;
  }

  private resizeGraph(chart: ChartJS, size: { width: number; height: number }) {
    const graphLayout = this.prepareGraphLayout();
    chart.data = this.createGraphData(graphLayout);
    chart.options = this.createGraphOptions(graphLayout);
    chart.update();
  }
}

const IoGraph = connect(mapStateToProps, mapDispatchToProps)(IoGraphComponent);

export default injectIntl(IoGraph);
